// THIS IS AN AUTOGENERATED FILE. DO NOT EDIT THIS FILE DIRECTLY.

import { useMutation, useMutationState } from '@tanstack/react-query';
import { useHttpClient } from '@toggl/sync/http';
import type { WorkspacePutOrgId } from '../types';
import type { MutationOptions } from '../utils/mutations';
import {
  putWorkspaceOrganizationIdMutationKey,
  uploadImageMutationKey,
} from './keys';
import { putWorkspaceOrganizationId, uploadImage } from './requests';

export type UploadImageMutationVariables = {
  workspaceId: number;
  upload: File;
};

export type UploadImageMutationOptions<TContext = unknown> = MutationOptions<
  typeof uploadImage,
  UploadImageMutationVariables,
  TContext
>;

/**
 * Uploads a multipart form with an image file into a public bucket. The image is deleted after a delay if not used. Images must be one of the following mime types: "image/jpeg", "image/png", "image/gif", "image/bmp", "image/webp".
 */
export function useUploadImageMutation<TContext = unknown>(
  options: UploadImageMutationOptions<TContext> = {}
) {
  const httpClient = useHttpClient();
  return useMutation({
    mutationFn: ({ workspaceId, upload }) =>
      uploadImage(httpClient, workspaceId, upload),
    mutationKey: uploadImageMutationKey,
    ...options,
  });
}

/** Returns the variables of a pending uploadImage mutation. */
export function useUploadImageMutationVariables() {
  return useMutationState({
    filters: { mutationKey: uploadImageMutationKey, status: 'pending' },
    select: (mutation) =>
      mutation.state.variables as UploadImageMutationVariables,
  });
}

export type PutWorkspaceOrganizationIdMutationVariables = {
  workspaceId: number;
  data: WorkspacePutOrgId;
};

export type PutWorkspaceOrganizationIdMutationOptions<TContext = unknown> =
  MutationOptions<
    typeof putWorkspaceOrganizationId,
    PutWorkspaceOrganizationIdMutationVariables,
    TContext
  >;

/**
 * Updates or creates a workspace organization id matching the workspaceId.
 */
export function usePutWorkspaceOrganizationIdMutation<TContext = unknown>(
  options: PutWorkspaceOrganizationIdMutationOptions<TContext> = {}
) {
  const httpClient = useHttpClient();
  return useMutation({
    mutationFn: ({ workspaceId, data }) =>
      putWorkspaceOrganizationId(httpClient, workspaceId, data),
    mutationKey: putWorkspaceOrganizationIdMutationKey,
    ...options,
  });
}

/** Returns the variables of a pending putWorkspaceOrganizationId mutation. */
export function usePutWorkspaceOrganizationIdMutationVariables() {
  return useMutationState({
    filters: {
      mutationKey: putWorkspaceOrganizationIdMutationKey,
      status: 'pending',
    },
    select: (mutation) =>
      mutation.state.variables as PutWorkspaceOrganizationIdMutationVariables,
  });
}
